import React, { useCallback, useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MotifIconButton } from '../button/icon-button/icon-button';
import { ReactComponent as NavigationIcFirstPage24px } from '../../assets/icons/navigation/ic_first_page_24px.svg';
import { ReactComponent as NavigationIcChevronLeft24px } from '../../assets/icons/navigation/ic_chevron_left_24px.svg';
import { ReactComponent as NavigationIcChevronRight24px } from '../../assets/icons/navigation/ic_chevron_right_24px.svg';
import { ReactComponent as NavigationIcLastPage24px } from '../../assets/icons/navigation/ic_last_page_24px.svg';

let nextUniqueId;
nextUniqueId = 0;

const defaultIcons = {
  first: <NavigationIcFirstPage24px />,
  last: <NavigationIcLastPage24px />,
  next: <NavigationIcChevronRight24px />,
  previous: <NavigationIcChevronLeft24px />
};

const pxToRem = px => px / parseFloat(getComputedStyle(document.documentElement).fontSize);

/**
 * @typedef Props
 * @prop {React.ReactNode} [children]
 * @prop {string} [className]
 * @prop {string | number} [currentPage]
 * @prop {React.ReactNode} [firstButton]
 * @prop {React.ReactNode} [lastButton]
 * @prop {React.ReactNode} [prevButton]
 * @prop {React.ReactNode} [nextButton]
 * @prop {(x: string | number) => void} [onPageChange]
 * @prop {number} [min]
 * @prop {number} max
 * @prop {boolean} [allowInput]
 * @prop {{}} [firstButtonProps]
 * @prop {{}} [lastButtonProps]
 * @prop {{}} [prevButtonProps]
 * @prop {{}} [nextButtonProps]
 * @prop {{}} [inputProps]
 * @prop {{first: React.ReactNode, last: React.ReactNode, next: React.ReactNode, previous: React.ReactNode}} [icons]
 */
/**
 * @param {Props & {[x: string]: any;}} props
 */
const MotifPagination = (props) => {
  const {
    className = '',
    children = null,
    currentPage = 1,
    firstButton = null,
    lastButton = null,
    prevButton = null,
    nextButton = null,
    onPageChange = () => null,
    min = 1,
    max,
    allowInput = false,
    firstButtonProps = {},
    lastButtonProps = {},
    prevButtonProps = {},
    nextButtonProps = {},
    inputProps = {},
    icons: userIcons = {},
    ...other
  } = props;
  const icons = {
    ...defaultIcons,
    ...userIcons
  };
  const [inputSize, setInputSize] = useState(9);
  const inputRef = useRef(null);
  const refInputElem = useRef(null);

  useEffect(() => {
    setInputSize(pxToRem(refInputElem.current.offsetWidth));
  }, [currentPage]);

  const classes = classNames({
    'motif-pagination': true,
    [className]: true
  });

  const countClasses = classNames({
    'motif-pagination-count': true,
    'motif-body-sm': true
  });

  const firstButtonOnClick = useCallback(() => onPageChange(min), [min]);
  const lastButtonOnClick = useCallback(() => onPageChange(max), [max]);
  const prevButtonOnClick = useCallback(() => onPageChange(currentPage - 1), [currentPage]);
  const nextButtonOnClick = useCallback(() => onPageChange(currentPage + 1), [currentPage]);

  const onPageInputChange = page => {
    if (page === '') {
      onPageChange('');

      return;
    }

    const pageNumber = Number(page);
    if (pageNumber >= min && pageNumber <= max) {
      onPageChange(pageNumber);
    }
  };

  const hasTitle = (objectProp) => objectProp && objectProp.title;

  return (
    <div {...other} className={classes} data-motif-pagination>
      {children}
      {firstButton ? (
        React.cloneElement(firstButton, {
          onClick: firstButtonOnClick,
          disabled: currentPage <= min,
          type: 'button',
          ...firstButtonProps
        })
      ) : (
        <MotifIconButton
          className="motif-pagination-button"
          disabled={currentPage <= min}
          aria-label={hasTitle(firstButtonProps) ? null : 'Go to first page'}
          type="button"
          onClick={firstButtonOnClick}
          {...firstButtonProps}
        >
          {icons.first}
        </MotifIconButton>
      )}

      {prevButton ? (
        React.cloneElement(prevButton, {
          onClick: prevButtonOnClick,
          disabled: currentPage <= min,
          type: 'button',
          ...prevButtonProps
        })
      ) : (
        <MotifIconButton
          className="motif-pagination-button"
          disabled={currentPage <= min}
          aria-label={hasTitle(prevButtonProps) ? null : 'Go to previous page'}
          type="button"
          onClick={prevButtonOnClick}
          {...prevButtonProps}
        >
          {icons.previous}
        </MotifIconButton>
      )}

      <div className={countClasses}>
        <span className="motif-number-of-pages">
          <span className="ref-input-elem" ref={refInputElem}>
            {currentPage}
          </span>
          <input
            style={{ width: `${inputSize}rem` }}
            className="motif-pagination-current-page"
            id={`pagination-current-page-${nextUniqueId++}`}
            ref={inputRef}
            value={currentPage}
            readOnly={!allowInput}
            max={max}
            min={min}
            type="number"
            onChange={event => onPageInputChange(event.target.value)}
            aria-label="current-page"
            {...inputProps}
          />
          /<span id={`pagination-max-pages-${nextUniqueId++}`}>{max}</span>
        </span>
      </div>

      {nextButton ? (
        React.cloneElement(nextButton, {
          onClick: nextButtonOnClick,
          disabled: currentPage >= max,
          type: 'button',
          ...nextButtonProps
        })
      ) : (
        <MotifIconButton
          className="motif-pagination-button"
          disabled={currentPage >= max}
          aria-label={hasTitle(nextButtonProps) ? null : 'Go to next page'}
          type="button"
          onClick={nextButtonOnClick}
          {...nextButtonProps}
        >
          {icons.next}
        </MotifIconButton>
      )}

      {lastButton ? (
        React.cloneElement(lastButton, {
          onClick: lastButtonOnClick,
          disabled: currentPage >= max,
          type: 'button',
          ...lastButtonProps
        })
      ) : (
        <MotifIconButton
          className="motif-pagination-button"
          disabled={currentPage >= max}
          aria-label={hasTitle(lastButtonProps) ? null : 'Go to last page'}
          type="button"
          onClick={lastButtonOnClick}
          {...lastButtonProps}
        >
          {icons.last}
        </MotifIconButton>
      )}
    </div>
  );
};

MotifPagination.propTypes = {
  className: PropTypes.string,
  currentPage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onPageChange: PropTypes.func,
  min: PropTypes.number,
  max: PropTypes.number.isRequired,
  firstButton: PropTypes.node,
  lastButton: PropTypes.node,
  prevButton: PropTypes.node,
  nextButton: PropTypes.node,
  allowInput: PropTypes.bool,
  children: PropTypes.node,
  firstButtonProps: PropTypes.instanceOf(Object),
  lastButtonProps: PropTypes.instanceOf(Object),
  prevButtonProps: PropTypes.instanceOf(Object),
  nextButtonProps: PropTypes.instanceOf(Object),
  inputProps: PropTypes.instanceOf(Object),
  icons: PropTypes.shape({
    first: PropTypes.node,
    last: PropTypes.node,
    next: PropTypes.node,
    previous: PropTypes.node
  })
};

export { MotifPagination };
